/**
 * sofa-configurator.js
 * Multi-step configurator popup (Size → Fabric → Seat interior).
 *
 * - Sizes are sibling products (custom.variant_product); selecting one
 *   navigates to that product carrying ?fabric=&interior= so the choice
 *   survives the page change.
 * - Fabric + Seat interior are variant options; price comes straight off
 *   the matched variant.
 * - Preview renders a <spin360-viewer> when the selected variant has a
 *   spin360.frameset (and the 360 app embed has defined the element),
 *   otherwise the variant image.
 * - Confirm syncs the PDP: hidden form input, price displays, URL
 *   ?variant= param, and fires `variant:changed` for other listeners.
 */
(function () {
  'use strict';

  function SofaConfigurator(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;

    var dataEl = document.getElementById('ConfiguratorData-' + this.sectionId);
    if (!dataEl) return;
    this.data = JSON.parse(dataEl.textContent);

    // The theme's ModalDialog moves itself to <body> on connect, so
    // resolve it by id and never assume it still lives inside the section.
    this.modal = document.getElementById('ConfiguratorModal-' + this.sectionId) ||
                 root.querySelector('[data-cfg-modal]');
    this.tiles = root.querySelector('[data-cfg-tiles]');
    this.state = {
      fabric: null,
      interior: null,
      leg: (this.data.legStyles && this.data.legStyles[0]) ? this.data.legStyles[0].name : null,
      scatter: {},
      bespokeDelta: 0,
      bespokeTicked: false,
      chip: 'all',
      step: 'fabric',
      swatchPicks: [],         // fabric names chosen for a free sample request
      covering: null,          // { range, colour, image, composition }
      coveringColour: 'all',   // base-colour filter
      coveringDesign: 'all'    // fabric-type filter
    };

    this.init();
  }

  SofaConfigurator.prototype.init = function () {
    var initial = this.findVariant(this.data.selectedVariantId) || this.data.variants[0];
    if (!initial) return;

    this.state.fabric = initial.options[this.data.fabricIndex];
    if (this.data.interiorIndex !== null) {
      this.state.interior = initial.options[this.data.interiorIndex];
    }

    this.initScatter();
    this.applyDeepLink();
    this.initBespoke();
    this.relocateTiles();
    this.relocatePreviewForMobile();
    this.buildChips();
    this.renderFabricGrid();
    this.buildCoveringFilters();
    this.renderCoverings();
    this.initStickyAtc();
    this.initSpinGridClicks();
    this.initSpinFullscreen();
    this.renderInteriorList();
    this.renderScatterGrids();
    this.bindEvents();
    this.updateAll(false);

    // The 360 app's script loads async and may finish AFTER this boot —
    // re-run the spin mounts the moment the element gets defined.
    if (window.customElements && customElements.whenDefined) {
      var self = this;
      customElements.whenDefined('spin360-viewer').then(function () {
        if (self.dead) return;
        self.updateHeroSpin();
        self.updatePreview();
      });
    }
  };


  /* Scoped queries across the section root, the relocated tiles and the
     relocated modal — these three can live in different parts of the DOM. */
  SofaConfigurator.prototype.q = function (sel) {
    var scopes = [this.modal, this.tiles, this.root];
    for (var i = 0; i < scopes.length; i++) {
      if (scopes[i]) {
        var hit = scopes[i].querySelector(sel);
        if (hit) return hit;
      }
    }
    return null;
  };
  SofaConfigurator.prototype.qa = function (sel) {
    var out = [];
    var scopes = [this.modal, this.tiles, this.root];
    var seen = [];
    scopes.forEach(function (scope) {
      if (!scope) return;
      scope.querySelectorAll(sel).forEach(function (el) {
        if (seen.indexOf(el) === -1) { seen.push(el); out.push(el); }
      });
    });
    return out;
  };

  /* ── Deep-linking ─────────────────────────────────────── */

  SofaConfigurator.prototype.applyDeepLink = function () {
    var params = new URLSearchParams(window.location.search);
    var fabric = params.get('fabric');
    var interior = params.get('interior');
    var changed = false;

    if (fabric) {
      var match = this.data.fabrics.filter(function (f) { return f.handle === fabric; })[0];
      if (match) { this.state.fabric = match.name; changed = true; }
    }
    if (interior && this.data.interiorIndex !== null) {
      var self = this;
      var values = this.interiorValues();
      values.forEach(function (v) {
        if (self.handleize(v) === interior) { self.state.interior = v; changed = true; }
      });
    }
    var leg = params.get('leg');
    if (leg && this.data.legStyles) {
      var legMatch = this.data.legStyles.filter(function (l) { return l.handle === leg; })[0];
      if (legMatch) this.state.leg = legMatch.name;
    }
    params.delete('leg');

    if (changed) {
      var variant = this.currentVariant();
      if (variant) this.syncProductForm(variant, true);
      // Tidy the URL: swap fabric/interior params for the canonical ?variant=
      params.delete('fabric');
      params.delete('interior');
      if (variant) params.set('variant', variant.id);
      var query = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : ''));
    }
  };

  /* ── Data helpers ─────────────────────────────────────── */

  SofaConfigurator.prototype.findVariant = function (id) {
    return this.data.variants.filter(function (v) { return v.id === id; })[0] || null;
  };

  SofaConfigurator.prototype.currentVariant = function () {
    var fi = this.data.fabricIndex;
    var ii = this.data.interiorIndex;
    var state = this.state;
    return this.data.variants.filter(function (v) {
      if (v.options[fi] !== state.fabric) return false;
      if (ii !== null && state.interior !== null && v.options[ii] !== state.interior) return false;
      return true;
    })[0] || null;
  };

  SofaConfigurator.prototype.variantsForFabric = function (fabric) {
    var fi = this.data.fabricIndex;
    return this.data.variants.filter(function (v) { return v.options[fi] === fabric; });
  };

  SofaConfigurator.prototype.interiorValues = function () {
    var ii = this.data.interiorIndex;
    if (ii === null) return [];
    var seen = [];
    this.data.variants.forEach(function (v) {
      if (seen.indexOf(v.options[ii]) === -1) seen.push(v.options[ii]);
    });
    return seen;
  };

  SofaConfigurator.prototype.handleize = function (str) {
    return String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  SofaConfigurator.prototype.formatMoney = function (pence) {
    var format = this.data.moneyFormat || '£{{amount}}';
    var amount = (pence / 100).toFixed(2).replace(/\.00$/, '');
    var withThousands = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(pence / 100).toLocaleString('en-GB'))
      .replace(/<[^>]+>/g, '')
      .trim();
  };

  SofaConfigurator.prototype.fabricMeta = function (name) {
    return this.data.fabrics.filter(function (f) { return f.name === name; })[0] || null;
  };

  // 0-based frame index for a 1-based, possibly out-of-range frame number —
  // wraps via modulo so a theme-editor value never points past the set.
  function spinGridFrameIndex0(frameset, frameNumber) {
    var n = parseInt(frameNumber, 10) || 1;
    return ((n - 1) % frameset.frameCount + frameset.frameCount) % frameset.frameCount;
  }

  // 360° gallery grid: 3 static frames (numbers set in the theme editor, on
  // the grid wrapper as data-frame-1/2/3) from the SELECTED variant's own
  // spin set, plus the selected fabric's swatch photo. Mirrors the URL
  // pattern the spin360-viewer web component itself uses (baseUrl/size/
  // frame-NN.format) so no extra app call is needed. Hidden on variants
  // with no frameset.
  SofaConfigurator.prototype.updateSpinGrid = function (variant) {
    var grid = document.querySelector('[data-cfg-spin-grid]');
    if (!grid) return;
    var fs = variant.frameset;
    if (!fs || !fs.baseUrl || !fs.frameCount) {
      grid.hidden = true;
      return;
    }
    grid.hidden = false;

    var sizes = fs.sizes || [1024];
    var size = sizes.slice().sort(function (a, b) { return a - b; }).pop();
    var padWidth = Math.max(2, String(fs.frameCount).length);
    var format = fs.format || 'webp';

    function frameUrl(frameNumber) {
      var idxStr = String(spinGridFrameIndex0(fs, frameNumber) + 1);
      while (idxStr.length < padWidth) idxStr = '0' + idxStr;
      return fs.baseUrl + '/' + size + '/frame-' + idxStr + '.' + format;
    }

    [1, 2, 3].forEach(function (n) {
      var img = grid.querySelector('[data-cfg-spin-grid-frame="' + n + '"] [data-cfg-spin-grid-img]');
      if (!img) return;
      var url = frameUrl(grid.getAttribute('data-frame-' + n) || n);
      if (img.getAttribute('src') !== url) img.setAttribute('src', url);
    });

    var swatchImg = grid.querySelector('[data-cfg-spin-grid-swatch]');
    if (swatchImg) {
      var meta = this.fabricMeta(this.state.fabric);
      var src = (meta && (meta.swatchImageLarge || meta.fallbackImageLarge)) || variant.image;
      if (src && swatchImg.getAttribute('src') !== src) swatchImg.setAttribute('src', src);
    }
  };

  // Clicking a frame tile jumps the main 360 viewer to that exact frame
  // (switching it into spin mode first if a flat photo is showing), so the
  // shopper can keep dragging to spin from wherever they clicked in.
  SofaConfigurator.prototype.initSpinGridClicks = function () {
    var grid = document.querySelector('[data-cfg-spin-grid]');
    if (!grid) return;
    var self = this;
    grid.addEventListener('click', function (e) {
      var tile = e.target.closest('[data-cfg-spin-grid-frame]');
      if (!tile) return;
      var variant = self.currentVariant();
      var fs = variant && variant.frameset;
      if (!fs || !fs.frameCount) return;

      var n = tile.getAttribute('data-cfg-spin-grid-frame');
      var frameNumber = grid.getAttribute('data-frame-' + n) || n;
      var idx0 = spinGridFrameIndex0(fs, frameNumber);

      self.updateHeroSpin();
      self.showHeroSpin(true);
      if (self.heroSpin && self.heroSpin.frameset) {
        self.heroSpin._step(idx0 - self.heroSpin.current);
      }

      var heroMain = document.querySelector('[data-cfg-gallery-main]');
      if (heroMain) heroMain.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  // The spin360-viewer's own built-in fullscreen button calls the native
  // Fullscreen API, which is unreliable in practice — iOS Safari has never
  // supported requestFullscreen() on non-video elements, and some embedded/
  // sandboxed browser contexts reject it outright ("Permissions check
  // failed"), leaving the button appearing to do nothing. A CSS-only
  // overlay works everywhere and gives the same full-bleed spin view (see
  // sofaclub.co.uk), so intercept the click in the capture phase — before
  // the component's own bubble-phase handler runs — and take over.
  //
  // Root cause of "nothing happens at all" (found by inspecting a real
  // click's e.target): .spin360-controls (the +/−/⛶ buttons) sits INSIDE
  // .spin360-stage, and stage's own pointerdown handler unconditionally
  // starts a drag and calls stage.setPointerCapture() for ANY pointerdown
  // within it — including on the buttons. Once stage has pointer capture,
  // Chromium retargets the synthesized click event to stage too, so it
  // never reaches the button at all — not the fullscreen button's own
  // handler, and not this listener either. Fix: stop pointerdown from ever
  // reaching stage's listener when it starts on a control button, so the
  // click resolves to the button as normal.
  SofaConfigurator.prototype.initSpinFullscreen = function () {
    var self = this;
    document.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.spin360-controls')) e.stopPropagation();
    }, true);
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.spin360-fs');
      if (!btn) return;
      var viewer = btn.closest('spin360-viewer');
      if (!viewer) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self.openSpinOverlay(viewer);
    }, true);
  };

  SofaConfigurator.prototype.openSpinOverlay = function (viewer) {
    if (this.spinOverlay) return;

    var overlay = document.createElement('div');
    overlay.className = 'cfg-spin-overlay';
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'cfg-spin-overlay__close';
    closeBtn.setAttribute('aria-label', 'Close full screen view');
    closeBtn.innerHTML = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);

    this.spinOverlay = overlay;
    this.spinOverlayViewer = viewer;
    this.spinOverlayOrigin = viewer.parentNode;
    this.spinOverlayNextSibling = viewer.nextSibling;

    // Inline styles beat any class the viewer already carries (e.g. the
    // hero's .cfg-hero-spin aspect-ratio: 4/3), no specificity juggling.
    viewer.style.width = '100%';
    viewer.style.height = '100%';
    viewer.style.aspectRatio = 'auto';
    overlay.insertBefore(viewer, closeBtn);

    document.body.classList.add('cfg-spin-overlay-open');

    var self = this;
    closeBtn.addEventListener('click', function () { self.closeSpinOverlay(); });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) self.closeSpinOverlay();
    });
    this._spinOverlayKeydown = function (e) {
      if (e.key === 'Escape') self.closeSpinOverlay();
    };
    document.addEventListener('keydown', this._spinOverlayKeydown);

    if (viewer._scheduleRedraw) viewer._scheduleRedraw();
    closeBtn.focus();
  };

  SofaConfigurator.prototype.closeSpinOverlay = function () {
    if (!this.spinOverlay) return;
    var viewer = this.spinOverlayViewer;

    if (viewer) {
      viewer.style.width = '';
      viewer.style.height = '';
      viewer.style.aspectRatio = '';
      if (this.spinOverlayOrigin) {
        this.spinOverlayOrigin.insertBefore(viewer, this.spinOverlayNextSibling);
      }
    }

    this.spinOverlay.remove();
    document.body.classList.remove('cfg-spin-overlay-open');
    if (this._spinOverlayKeydown) document.removeEventListener('keydown', this._spinOverlayKeydown);

    this.spinOverlay = null;
    this.spinOverlayViewer = null;
    this.spinOverlayOrigin = null;
    this.spinOverlayNextSibling = null;

    if (viewer && viewer._scheduleRedraw) viewer._scheduleRedraw();
  };

  /* ── DOM construction ─────────────────────────────────── */

  // Move the summary tiles into the hero info column, under the price block.
  SofaConfigurator.prototype.relocateTiles = function () {
    var tiles = this.tiles;
    var heroInfo = document.querySelector('.sofa-hero__info');
    if (!tiles || !heroInfo) return;
    var anchor = heroInfo.querySelector('.sofa-hero__finance-text') ||
                 heroInfo.querySelector('.sofa-hero__price-block');
    if (anchor && anchor.nextSibling) {
      heroInfo.insertBefore(tiles, anchor.nextSibling);
    } else {
      heroInfo.appendChild(tiles);
    }
  };

  // On mobile the popup stacks step panels above the summary rail — move
  // just the product preview (image/360) up above the step panels so the
  // customer can still see it while scrolling through choices, without
  // touching the desktop side-by-side layout at all.
  SofaConfigurator.prototype.relocatePreviewForMobile = function () {
    var self = this;
    var preview = this.q('[data-cfg-preview]');
    var body = this.q('.cfg-modal__body');
    var steps = this.q('.cfg-steps');
    if (!preview || !body || !steps) return;

    var homeParent = preview.parentNode;
    var homeNext = preview.nextSibling;

    function apply(isMobile) {
      if (isMobile) {
        if (preview.parentNode !== body || preview.nextSibling !== steps) {
          body.insertBefore(preview, steps);
        }
      } else if (preview.parentNode !== homeParent) {
        if (homeNext) homeParent.insertBefore(preview, homeNext);
        else homeParent.appendChild(preview);
      }
    }

    var mq = window.matchMedia('(max-width: 900px)');
    apply(mq.matches);
    var listener = function (e) { apply(e.matches); };
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else if (mq.addListener) mq.addListener(listener); // Safari <14 fallback
  };

  // Sticky Add to Basket: mirrors the real button, appearing only once that
  // button leaves the viewport. It never holds its own inputs — clicking it
  // submits the real form so all line item properties travel with it.
  SofaConfigurator.prototype.initStickyAtc = function () {
    var bar = document.querySelector('[data-cfg-sticky-atc]');
    if (!bar) return;
    var form = document.querySelector('form[action*="/cart/add"]');
    var realBtn = form && form.querySelector('.cfg-btn--atb');
    if (!form || !realBtn) return;

    var self = this;
    bar.addEventListener('click', function (e) {
      if (!e.target.closest('[data-cfg-sticky-add]')) return;
      // Route through the real button so the bespoke submit interceptor and
      // native validation behave exactly as they do for a normal add.
      realBtn.click();
    });

    function setShown(shown) {
      bar.hidden = !shown;
      bar.classList.toggle('is-visible', shown);
      document.body.classList.toggle('has-cfg-sticky-atc', shown);
    }

    // The popup is a fixed, full-screen overlay at z-index 1000; the bar sits
    // below it, so it's covered automatically with no open/close bookkeeping.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { setShown(!entry.isIntersecting); });
      }, { rootMargin: '0px 0px -80px 0px' });
      io.observe(realBtn);
      this._stickyObserver = io;
    }
  };

  SofaConfigurator.prototype.buildChips = function () {
    var container = this.q('[data-cfg-chips]');
    if (!container) return;

    var colours = [];
    this.data.fabrics.forEach(function (f) {
      if (f.baseColour && colours.indexOf(f.baseColour) === -1) colours.push(f.baseColour);
    });

    if (colours.length < 2) { container.hidden = true; return; }

    var self = this;
    var html = '<button type="button" class="cfg-chip is-active" data-cfg-chip="all">All</button>';
    colours.forEach(function (c) {
      var sample = self.data.fabrics.filter(function (f) { return f.baseColour === c; })[0];
      var dot = '';
      if (sample) {
        if (sample.swatchImage) dot = '<span class="cfg-chip__dot" style="background-image:url(' + sample.swatchImage + ')"></span>';
        else if (sample.swatchColor) dot = '<span class="cfg-chip__dot" style="background-color:rgb(' + sample.swatchColor + ')"></span>';
      }
      html += '<button type="button" class="cfg-chip" data-cfg-chip="' + self.handleize(c) + '" data-colour="' + c + '">' + dot + c + '</button>';
    });
    container.innerHTML = html;
  };

  // Fabric names often end in a trailing "( Band C )"-style suffix — keep
  // that whole parenthetical together on its own line instead of letting
  // it wrap mid-phrase (e.g. "Band D" / ")" split across two lines).
  SofaConfigurator.prototype.formatSwatchName = function (name) {
    var match = name.match(/^(.*?)\s*(\([^)]*\))\s*$/);
    if (!match) return name;
    return match[1] + ' <span class="cfg-swatch__band">' + match[2] + '</span>';
  };

  /* ── Free swatch samples ──────────────────────────────── */

  SofaConfigurator.prototype.openSwatchRequest = function () {
    if (!this.data.swatchRequest) return;
    this.state.swatchPicks = [];
    // Forced: on a single-fabric product the fabric panel has no stepper
    // entry and exists only for this flow.
    this.open('fabric', true);
    var panel = this.q('[data-cfg-swatchreq]');
    if (panel) panel.hidden = false;
    // Hide the normal fabric UI so the £0 sample flow can't be confused
    // with choosing the sofa's own fabric.
    this.toggleFabricChrome(false);
    this.renderSwatchRequest();
  };

  SofaConfigurator.prototype.closeSwatchRequest = function () {
    var panel = this.q('[data-cfg-swatchreq]');
    if (panel) panel.hidden = true;
    this.state.swatchPicks = [];
    this.toggleFabricChrome(true);
    this.renderCoverings();
  };

  SofaConfigurator.prototype.swatchRequestOpen = function () {
    var panel = this.q('[data-cfg-swatchreq]');
    return !!panel && !panel.hidden;
  };

  // Everything in the fabric step that belongs to the sofa itself, plus the
  // rail's Confirm flow — all irrelevant while requesting free samples.
  SofaConfigurator.prototype.toggleFabricChrome = function (show) {
    var sels = ['[data-cfg-fabric-grid]', '[data-cfg-chips]', '[data-cfg-coverings]'];
    var self = this;
    sels.forEach(function (s) {
      var el = self.q(s);
      if (!el) return;
      if (s === '[data-cfg-coverings]') {
        if (show) { self.renderCoverings(); } else { el.hidden = true; }
      } else {
        el.hidden = !show;
      }
    });
    var title = this.q('[data-cfg-step="fabric"] .cfg-step__title');
    if (title) title.hidden = !show;
    var rail = this.q('.cfg-rail');
    if (rail) rail.classList.toggle('is-swatchreq', !show);
  };

  SofaConfigurator.prototype.renderSwatchRequest = function () {
    var cfg = this.data.swatchRequest;
    var grid = this.q('[data-cfg-swatchreq-grid]');
    if (!cfg || !grid) return;
    var self = this;
    var picks = this.state.swatchPicks;

    grid.innerHTML = this.data.fabrics.map(function (fabric) {
      // The coverings placeholder isn't a real fabric — nothing to post out.
      if (self.data.coverings &&
          fabric.name.toLowerCase() === (self.data.coverings.optionValue || '').toLowerCase()) return '';

      var idx = picks.indexOf(fabric.name);
      var chosen = idx !== -1;
      var full = picks.length >= cfg.max && !chosen;

      var tile;
      if (fabric.swatchImage) tile = '<img src="' + fabric.swatchImage + '" alt="" loading="lazy">';
      else if (fabric.swatchColor) tile = '<span class="cfg-swatch__fill" style="background-color:rgb(' + fabric.swatchColor + ')"></span>';
      else if (fabric.fallbackImage) tile = '<img src="' + fabric.fallbackImage + '" alt="" loading="lazy">';
      else tile = '<span class="cfg-swatch__fill">' + fabric.name.charAt(0) + '</span>';

      return '<button type="button" class="cfg-swatch' + (chosen ? ' is-selected' : '') +
        (full ? ' is-disabled' : '') + '" data-cfg-swatchreq-pick="' +
        fabric.name.replace(/"/g, '&quot;') + '"' + (full ? ' disabled' : '') + '>' +
        '<span class="cfg-swatch__tile">' +
          '<span class="cfg-swatch__cutout">' + tile + '</span>' +
          (chosen ? '<span class="cfg-check cfg-check--num">' + (idx + 1) + '</span>' : '') +
        '</span>' +
        '<span class="cfg-swatch__name">' + self.formatSwatchName(fabric.name) + '</span>' +
        '</button>';
    }).join('');

    var count = this.q('[data-cfg-swatchreq-count]');
    if (count) {
      count.textContent = picks.length
        ? picks.length + ' of ' + cfg.max + ' selected — ' + picks.join(', ')
        : 'Select up to ' + cfg.max + ' swatches';
    }
    var submit = this.q('[data-cfg-swatchreq-submit]');
    if (submit) submit.disabled = picks.length === 0;
  };

  SofaConfigurator.prototype.toggleSwatchPick = function (name) {
    var cfg = this.data.swatchRequest;
    var picks = this.state.swatchPicks;
    var i = picks.indexOf(name);
    if (i !== -1) picks.splice(i, 1);
    else if (picks.length < cfg.max) picks.push(name);
    this.renderSwatchRequest();
  };

  SofaConfigurator.prototype.submitSwatchRequest = function () {
    var cfg = this.data.swatchRequest;
    var picks = this.state.swatchPicks;
    if (!cfg || !picks.length) return;

    var self = this;
    var btn = this.q('[data-cfg-swatchreq-submit]');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

    var props = {};
    props[cfg.sourceProperty] = cfg.sourceValue;
    picks.forEach(function (name, i) {
      props[cfg.propertyPrefix + ' ' + (i + 1)] = name;
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: cfg.variantId, quantity: 1, properties: props }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        self.closeSwatchRequest();
        self.close();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        var drawer = document.getElementById('Cart-Drawer');
        if (drawer && drawer.open) drawer.open();
        else window.location.href = '/cart';
      })
      .catch(function () {
        window.location.href = '/cart';
      });
  };

  /* ── Additional coverings ─────────────────────────────── */

  // Liquid hands us the raw taxonomy id; these are Shopify's standard colour
  // values, named to match the wording already used by the fabric chips.
  var TAXONOMY_COLOURS = {
    '1': 'Black', '2': 'Blue', '3': 'White', '4': 'Gold', '5': 'Silver',
    '6': 'Beige', '7': 'Brown', '8': 'Grey', '9': 'Green', '10': 'Orange',
    '11': 'Pink', '12': 'Purple', '13': 'Red', '14': 'Yellow', '15': 'Navy',
    '16': 'Rose Gold', '17': 'Clear', '657': 'Bronze', '2865': 'Multi'
  };

  // Flattened once, then filtered — every colour carries its range's
  // design/composition so a filtered view can still label each result.
  SofaConfigurator.prototype.coveringItems = function () {
    if (this._coveringItems) return this._coveringItems;
    var out = [];
    (this.data.coverings ? this.data.coverings.ranges : []).forEach(function (rng) {
      (rng.colours || []).forEach(function (col) {
        out.push({
          range: rng.name, design: rng.design, composition: rng.composition,
          description: rng.description, name: col.name, image: col.image,
          base: TAXONOMY_COLOURS[col.baseId] || '', hex: col.hex || ''
        });
      });
    });
    this._coveringItems = out;
    return out;
  };

  SofaConfigurator.prototype.coveringsActive = function () {
    var c = this.data.coverings;
    if (!c) return false;
    return (this.state.fabric || '').toLowerCase() === (c.optionValue || '').toLowerCase();
  };

  SofaConfigurator.prototype.buildCoveringFilters = function () {
    var items = this.coveringItems();
    var self = this;

    function chips(container, values, active, attr) {
      var el = self.q(container);
      if (!el) return;
      var html = '<button type="button" class="cfg-chip' + (active === 'all' ? ' is-active' : '') +
                 '" ' + attr + '="all">All</button>';
      values.forEach(function (v) {
        var h = self.handleize(v);
        html += '<button type="button" class="cfg-chip' + (active === h ? ' is-active' : '') +
                '" ' + attr + '="' + h + '">' + v + '</button>';
      });
      el.innerHTML = html;
    }

    function uniq(key) {
      var seen = [];
      items.forEach(function (i) {
        if (i[key] && seen.indexOf(i[key]) === -1) seen.push(i[key]);
      });
      return seen.sort();
    }

    chips('[data-cfg-coverings-colour]', uniq('base'), this.state.coveringColour, 'data-cfg-cov-colour');
    chips('[data-cfg-coverings-design]', uniq('design'), this.state.coveringDesign, 'data-cfg-cov-design');
  };

  SofaConfigurator.prototype.renderCoverings = function () {
    var wrap = this.q('[data-cfg-coverings]');
    if (!wrap) return;

    var active = this.coveringsActive();
    wrap.hidden = !active;
    if (!active) return;

    var self = this;
    var colourF = this.state.coveringColour;
    var designF = this.state.coveringDesign;
    var filtering = colourF !== 'all' || designF !== 'all';

    var matches = this.coveringItems().filter(function (i) {
      if (colourF !== 'all' && self.handleize(i.base) !== colourF) return false;
      if (designF !== 'all' && self.handleize(i.design) !== designF) return false;
      return true;
    });

    var countEl = this.q('[data-cfg-coverings-count]');
    if (countEl) {
      countEl.textContent = matches.length + (matches.length === 1 ? ' fabric' : ' fabrics') +
        (filtering ? ' match your filters' : ' available');
    }

    var results = this.q('[data-cfg-coverings-results]');
    if (!results) return;

    if (!matches.length) {
      results.innerHTML = '<p class="cfg-empty">No coverings match those filters.</p>';
      return;
    }

    // Filtering flattens across every range so the customer sees all the
    // greys (or all the boucles) at once; unfiltered stays grouped by range.
    results.innerHTML = filtering
      ? '<div class="cfg-swatch-grid">' + matches.map(function (i) { return self.coveringCard(i, true); }).join('') + '</div>'
      : this.data.coverings.ranges.map(function (rng) {
          var cols = (rng.colours || []);
          if (!cols.length) return '';
          return '<section class="cfg-cov-range">' +
            '<header class="cfg-cov-range__head">' +
              '<h4 class="cfg-cov-range__name">' + rng.name + '</h4>' +
              '<p class="cfg-cov-range__meta">' +
                [rng.design, rng.composition].filter(Boolean).join(' &middot; ') +
              '</p>' +
              (rng.description
                ? '<details class="cfg-cov-range__details"><summary>Details</summary><p>' +
                  rng.description + '</p></details>'
                : '') +
            '</header>' +
            '<div class="cfg-swatch-grid">' +
              cols.map(function (col) {
                return self.coveringCard({
                  range: rng.name, design: rng.design, composition: rng.composition,
                  name: col.name, image: col.image,
                  base: TAXONOMY_COLOURS[col.baseId] || '', hex: col.hex
                }, false);
              }).join('') +
            '</div>' +
          '</section>';
        }).join('');
  };

  SofaConfigurator.prototype.coveringCard = function (item, showRange) {
    var sel = this.state.covering;
    var selected = !!sel && sel.range === item.range && sel.colour === item.name;

    var tile;
    if (item.image) tile = '<img src="' + item.image + '" alt="" loading="lazy">';
    else if (item.hex) tile = '<span class="cfg-swatch__fill" style="background-color:' + item.hex + '"></span>';
    else tile = '<span class="cfg-swatch__fill">' + (item.name || '?').charAt(0) + '</span>';

    return '<button type="button" class="cfg-swatch' + (selected ? ' is-selected' : '') + '"' +
      ' data-cfg-covering-range="' + String(item.range).replace(/"/g, '&quot;') + '"' +
      ' data-cfg-covering-colour="' + String(item.name).replace(/"/g, '&quot;') + '">' +
      '<span class="cfg-swatch__tile">' +
        '<span class="cfg-swatch__cutout">' + tile + '</span>' +
        (selected ? '<span class="cfg-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span>' : '') +
      '</span>' +
      '<span class="cfg-swatch__name">' + item.name + '</span>' +
      (showRange ? '<span class="cfg-swatch__price">' + item.range + '</span>' : '') +
      '</button>';
  };

  SofaConfigurator.prototype.coveringLabel = function () {
    var c = this.state.covering;
    return c ? c.range + ' — ' + c.colour : '';
  };

  SofaConfigurator.prototype.renderFabricGrid = function () {
    var grid = this.q('[data-cfg-fabric-grid]');
    if (!grid) return;
    var self = this;
    var html = '';

    this.data.fabrics.forEach(function (fabric) {
      if (self.state.chip !== 'all' && self.handleize(fabric.baseColour) !== self.state.chip) return;

      var variants = self.variantsForFabric(fabric.name);
      if (!variants.length) return;
      var minPrice = Math.min.apply(null, variants.map(function (v) { return v.price; }));
      var anyAvailable = variants.some(function (v) { return v.available; });
      var selected = fabric.name === self.state.fabric;

      var tile;
      if (fabric.swatchImage) tile = '<img src="' + fabric.swatchImage + '" alt="" loading="lazy">';
      else if (fabric.swatchColor) tile = '<span class="cfg-swatch__fill" style="background-color:rgb(' + fabric.swatchColor + ')"></span>';
      else if (fabric.fallbackImage) tile = '<img src="' + fabric.fallbackImage + '" alt="" loading="lazy">';
      else tile = '<span class="cfg-swatch__fill">' + fabric.name.charAt(0) + '</span>';

      html += '<button type="button" class="cfg-swatch' + (selected ? ' is-selected' : '') + (anyAvailable ? '' : ' is-oos') + '" data-cfg-fabric="' + fabric.name.replace(/"/g, '&quot;') + '">' +
        '<span class="cfg-swatch__tile">' +
        '<span class="cfg-swatch__cutout">' + tile + '</span>' +
        (selected ? '<span class="cfg-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span>' : '') +
        '</span>' +
        '<span class="cfg-swatch__name">' + self.formatSwatchName(fabric.name) + '</span>' +
        '<span class="cfg-swatch__price">from ' + self.formatMoney(minPrice) + '</span>' +
        '</button>';
    });

    grid.innerHTML = html || '<p class="cfg-empty">No fabrics in this colour.</p>';
  };

  SofaConfigurator.prototype.renderInteriorList = function () {
    var list = this.q('[data-cfg-interior-list]');
    if (!list || this.data.interiorIndex === null) return;
    var self = this;
    var values = this.interiorValues();
    var fabricVariants = this.variantsForFabric(this.state.fabric);
    var basePrice = Math.min.apply(null, fabricVariants.map(function (v) { return v.price; }));
    var html = '';

    values.forEach(function (value) {
      var variant = fabricVariants.filter(function (v) {
        return v.options[self.data.interiorIndex] === value;
      })[0];
      if (!variant) return;

      var delta = variant.price - basePrice;
      var deltaLabel = delta > 0 ? '+' + self.formatMoney(delta) : 'Included';
      var selected = value === self.state.interior;

      html += '<button type="button" class="cfg-interior' + (selected ? ' is-selected' : '') + '" data-cfg-interior="' + value.replace(/"/g, '&quot;') + '">' +
        '<span class="cfg-interior__name">' + value + '</span>' +
        '<span class="cfg-interior__delta">' + deltaLabel + '</span>' +
        (selected ? '<span class="cfg-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span>' : '') +
        '</button>';
    });

    list.innerHTML = html;
  };

  /* ── Scatter cushions (line item properties, no variant/price impact) ── */

  // Each cushion entry (e.g. "Large Scatters") defaults to the product's
  // own first Fabric option value — matches the server-rendered default
  // in the hidden input/rail summary so there's no mismatch on first paint.
  SofaConfigurator.prototype.initScatter = function () {
    var self = this;
    var defaultFabric = (this.data.fabrics && this.data.fabrics[0]) ? this.data.fabrics[0].name : null;
    (this.data.scatterCushions || []).forEach(function (cushion, idx) {
      self.state.scatter[idx] = defaultFabric;
    });
  };

  SofaConfigurator.prototype.renderScatterGrids = function () {
    var self = this;
    (this.data.scatterCushions || []).forEach(function (cushion, idx) {
      self.renderScatterGroup(idx, self.data.fabrics, '[data-cfg-scatter-plain="' + idx + '"]');
      self.renderScatterGroup(idx, self.data.scatterPatterned, '[data-cfg-scatter-patterned="' + idx + '"]');
    });
  };

  SofaConfigurator.prototype.renderScatterGroup = function (cushionIndex, items, selector) {
    var grid = this.q(selector);
    if (!grid) return;
    var self = this;
    var chosen = this.state.scatter[cushionIndex];
    var html = '';

    (items || []).forEach(function (item) {
      var label = item.name || item.label; // fabrics use .name, scatterPatterned uses .label
      var selected = chosen === label;
      var swatchImage = item.swatchImage || item.image || item.fallbackImage;
      var swatchColor = item.swatchColor || item.color;

      var tile;
      if (swatchImage) {
        tile = '<img src="' + swatchImage + '" alt="" loading="lazy">';
      } else if (swatchColor) {
        var css = /^#/.test(swatchColor) ? swatchColor : 'rgb(' + swatchColor + ')';
        tile = '<span class="cfg-swatch__fill" style="background-color:' + css + '"></span>';
      } else {
        tile = '<span class="cfg-swatch__fill">' + label.charAt(0) + '</span>';
      }

      html += '<button type="button" class="cfg-swatch' + (selected ? ' is-selected' : '') + '" data-cfg-scatter-cushion="' + cushionIndex + '" data-cfg-scatter-fabric="' + label.replace(/"/g, '&quot;') + '">' +
        '<span class="cfg-swatch__tile">' +
        '<span class="cfg-swatch__cutout">' + tile + '</span>' +
        (selected ? '<span class="cfg-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span>' : '') +
        '</span>' +
        '<span class="cfg-swatch__name">' + self.formatSwatchName(label) + '</span>' +
        '</button>';
    });

    grid.innerHTML = html || '<p class="cfg-empty">No options available.</p>';
  };

  // Look up display metadata (image/color) for a chosen scatter fabric —
  // checked against Plain (product's own fabrics) first, then Patterned.
  SofaConfigurator.prototype.scatterFabricMeta = function (label) {
    var plain = (this.data.fabrics || []).filter(function (f) { return f.name === label; })[0];
    if (plain) {
      return {
        image: plain.swatchImage || plain.fallbackImage,
        color: plain.swatchColor ? 'rgb(' + plain.swatchColor + ')' : null
      };
    }
    var patterned = (this.data.scatterPatterned || []).filter(function (f) { return f.label === label; })[0];
    if (patterned) return { image: patterned.image, color: patterned.color };
    return null;
  };

  /* ── Events ───────────────────────────────────────────── */

  SofaConfigurator.prototype.bindEvents = function () {
    var self = this;

    // Close when clicking the dimmed overlay (the modal element itself)
    if (this.modal) {
      this.modal.addEventListener('click', function (e) {
        if (e.target === self.modal) self.close();
      });
    }

    // Keyboard activation for non-button clickables (summary rows)
    document.addEventListener('keydown', function (e) {
      if (self.dead) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var row = e.target.closest('[data-cfg-open][role="button"]');
      if (row) { e.preventDefault(); self.open(row.dataset.cfgOpen); }
    });

    // Document-level delegation: the summary tiles are relocated into the
    // hero info column (outside this.root), so a root-scoped listener
    // would never hear their clicks.
    document.addEventListener('click', function (e) {
      if (self.dead) return; // superseded by a background size switch
      var opener = e.target.closest('[data-cfg-open]');
      if (opener) {
        self.open(opener.dataset.cfgOpen);
        if (opener.dataset.cfgOpenSub !== undefined) self.scrollToScatterEntry(opener.dataset.cfgOpenSub);
        return;
      }

      var stepBtn = e.target.closest('[data-cfg-step-btn]');
      if (stepBtn) {
        // Moving to another step ends sample-request mode.
        if (self.swatchRequestOpen()) self.closeSwatchRequest();
        self.goToStep(stepBtn.dataset.cfgStepBtn);
        return;
      }

      var chip = e.target.closest('[data-cfg-chip]');
      if (chip) {
        self.state.chip = chip.dataset.cfgChip;
        self.qa('[data-cfg-chip]').forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
        });
        self.renderFabricGrid();
        return;
      }

      if (e.target.closest('[data-cfg-swatchreq-open]')) { self.openSwatchRequest(); return; }
      if (e.target.closest('[data-cfg-swatchreq-cancel]')) {
        self.closeSwatchRequest();
        // Single-fabric product: the panel has nothing else in it, so
        // cancelling closes the popup rather than leaving it blank.
        if (self.navigableSteps().indexOf('fabric') === -1) self.close();
        return;
      }
      if (e.target.closest('[data-cfg-swatchreq-submit]')) { self.submitSwatchRequest(); return; }

      var swatchPick = e.target.closest('[data-cfg-swatchreq-pick]');
      if (swatchPick) {
        self.toggleSwatchPick(swatchPick.dataset.cfgSwatchreqPick);
        return;
      }

      var covColour = e.target.closest('[data-cfg-cov-colour]');
      if (covColour) {
        self.state.coveringColour = covColour.dataset.cfgCovColour;
        self.buildCoveringFilters();
        self.renderCoverings();
        return;
      }

      var covDesign = e.target.closest('[data-cfg-cov-design]');
      if (covDesign) {
        self.state.coveringDesign = covDesign.dataset.cfgCovDesign;
        self.buildCoveringFilters();
        self.renderCoverings();
        return;
      }

      var covering = e.target.closest('[data-cfg-covering-range]');
      if (covering) {
        self.state.covering = {
          range: covering.dataset.cfgCoveringRange,
          colour: covering.dataset.cfgCoveringColour
        };
        self.renderCoverings();
        self.updateAll(true);
        return;
      }

      var swatch = e.target.closest('[data-cfg-fabric]');
      if (swatch) {
        self.state.fabric = swatch.dataset.cfgFabric;
        // Leaving the coverings option clears any covering already picked,
        // so a standard fabric can never carry one through to the cart.
        if (!self.coveringsActive()) self.state.covering = null;
        // Keep the interior if it exists for this fabric, else first available
        if (self.data.interiorIndex !== null && !self.currentVariant()) {
          var fv = self.variantsForFabric(self.state.fabric)[0];
          if (fv) self.state.interior = fv.options[self.data.interiorIndex];
        }
        self.renderFabricGrid();
        self.renderCoverings();
        self.renderInteriorList();
        self.updateAll(true);
        return;
      }

      var interior = e.target.closest('[data-cfg-interior]');
      if (interior) {
        self.state.interior = interior.dataset.cfgInterior;
        self.renderInteriorList();
        self.updateAll(true);
        return;
      }

      var leg = e.target.closest('[data-cfg-leg]');
      if (leg) {
        self.state.leg = leg.dataset.cfgLeg;
        self.qa('[data-cfg-leg]').forEach(function (card) {
          card.classList.toggle('is-selected', card === leg);
        });
        self.updateAll(true);
        return;
      }

      var scatterSwatch = e.target.closest('[data-cfg-scatter-fabric]');
      if (scatterSwatch) {
        var sIdx = scatterSwatch.dataset.cfgScatterCushion;
        self.state.scatter[sIdx] = scatterSwatch.dataset.cfgScatterFabric;
        self.renderScatterGroup(sIdx, self.data.fabrics, '[data-cfg-scatter-plain="' + sIdx + '"]');
        self.renderScatterGroup(sIdx, self.data.scatterPatterned, '[data-cfg-scatter-patterned="' + sIdx + '"]');
        self.updateAll(false);
        return;
      }

      var sizeLink = e.target.closest('[data-cfg-size-link]');
      if (sizeLink) {
        e.preventDefault();
        if (sizeLink.getAttribute('aria-current') === 'true') {
          // Re-picking the current size just advances — to whichever step
          // actually follows Size on this product (Fabric may be absent).
          var steps = self.navigableSteps();
          var next = steps[steps.indexOf('size') + 1];
          if (next) self.goToStep(next);
          return;
        }
        var url = new URL(sizeLink.href, window.location.origin);
        url.searchParams.set('fabric', self.handleize(self.state.fabric));
        if (self.state.interior) url.searchParams.set('interior', self.handleize(self.state.interior));
        if (self.state.leg) url.searchParams.set('leg', self.handleize(self.state.leg));
        // Carry over theme/template preview params (dev + theme editor)
        var current = new URLSearchParams(window.location.search);
        ['view', 'preview_theme_id', '_ab', '_fd', '_sc'].forEach(function (key) {
          if (current.has(key)) url.searchParams.set(key, current.get(key));
        });
        self.switchSizeProduct(url.toString());
        return;
      }

      if (e.target.closest('[data-cfg-confirm]')) { self.confirm(); return; }
      if (e.target.closest('[data-cfg-close]')) { self.close(); return; }

      var calendly = e.target.closest('[data-cfg-calendly]');
      if (calendly) {
        e.preventDefault();
        if (window.Calendly) {
          window.Calendly.initPopupWidget({ url: calendly.dataset.cfgCalendly });
        } else {
          // Widget script still loading/blocked — open the booking page directly
          window.open(calendly.dataset.cfgCalendly, '_blank');
        }
        return;
      }

      var anchor = e.target.closest('.cfg-info__buttons a[href^="#"]');
      if (anchor) {
        var target = document.querySelector(anchor.getAttribute('href'));
        if (target) {
          e.preventDefault();
          var collapsible = target.querySelector('details[data-sofa-collapsible]');
          if (collapsible) collapsible.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      var widthMode = e.target.closest('[data-cfg-width-mode]');
      if (widthMode) { self.setBespokeMode(widthMode.dataset.cfgWidthMode); return; }

      var preset = e.target.closest('[data-cfg-bespoke-preset]');
      if (preset) { self.setBespokeDelta(parseInt(preset.dataset.cfgBespokePreset, 10)); return; }

      var thumb = e.target.closest('[data-cfg-thumb]');
      if (thumb) { self.showGalleryImage(thumb); return; }
      if (e.target.closest('[data-cfg-gallery-prev]')) { self.stepGallery(-1); return; }
      if (e.target.closest('[data-cfg-gallery-next]')) { self.stepGallery(1); return; }
    });
  };

  SofaConfigurator.prototype.scrollToScatterEntry = function (idx) {
    var entry = this.q('[data-cfg-scatter-entry="' + idx + '"]');
    if (entry) entry.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  /* ── Gallery (main image, arrows, thumbnails) ─────────── */

  SofaConfigurator.prototype.showGalleryImage = function (thumb) {
    var img = document.querySelector('[data-cfg-main-image]');
    if (!img || !thumb) return;
    img.src = thumb.dataset.imageSrc;
    img.alt = thumb.dataset.imageAlt || '';
    img.removeAttribute('srcset');
    document.querySelectorAll('[data-cfg-thumb]').forEach(function (t) {
      t.classList.toggle('is-active', t === thumb);
    });
  };

  SofaConfigurator.prototype.stepGallery = function (dir) {
    var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-cfg-thumb]'));
    if (!thumbs.length) return;
    var current = thumbs.findIndex(function (t) { return t.classList.contains('is-active'); });
    var next = (current + dir + thumbs.length) % thumbs.length;
    this.showHeroSpin(false);
    this.showGalleryImage(thumbs[next]);
  };

  /* ── Background size switch ───────────────────────────── */

  // Fetch the sibling size's page, swap the page content and configurator
  // in place, and keep the popup open — no full navigation. Falls back to
  // a normal page load if anything goes wrong.
  SofaConfigurator.prototype.switchSizeProduct = function (url) {
    var self = this;
    if (this.switching) return;
    this.switching = true;
    if (this.modal) this.modal.classList.add('is-loading');

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newMain = doc.getElementById('main-content');
        var curMain = document.getElementById('main-content');
        if (!newMain || !curMain) throw new Error('main content not found');
        if (!newMain.querySelector('[data-sofa-configurator]')) throw new Error('no configurator on target');

        // Drop this instance's modal (ModalDialog moved it to <body>).
        document.querySelectorAll('body > [data-cfg-modal]').forEach(function (el) { el.remove(); });

        curMain.replaceWith(document.importNode(newMain, true));
        document.title = doc.title;
        window.history.pushState({ sofaConfigurator: true }, '', url);

        // Boot a fresh instance against the new DOM. Its deep-link pass
        // reads ?fabric=&interior= from the URL we just pushed, restores
        // the selection and normalises the URL to ?variant=.
        var previous = window.__cfgInstance;
        if (previous) {
          previous.dead = true;
          delete window.__cfgInstance;
        }
        boot();

        // Back/forward after an in-place swap needs a real load to stay
        // consistent — bind once, only after the first pushState.
        if (!window.__cfgPopstateBound) {
          window.__cfgPopstateBound = true;
          window.addEventListener('popstate', function () { window.location.reload(); });
        }

        var next = window.__cfgInstance;
        if (next && next !== previous) {
          next.open('size');
        } else {
          throw new Error('configurator failed to boot');
        }
      })
      .catch(function () {
        window.location.href = url; // graceful fallback: normal navigation
      })
      .then(function () {
        self.switching = false;
      });
  };

  /* ── Popup control ────────────────────────────────────── */

  SofaConfigurator.prototype.open = function (step, force) {
    if (!this.modal) return;
    // Opening the popup for the sofa itself always ends a sample request
    // left open by closing the popup with ✕ — otherwise the rail would
    // reappear stuck in sample mode.
    if (!force && this.swatchRequestOpen()) this.closeSwatchRequest();
    if (typeof this.modal.show === 'function') this.modal.show();
    else { this.modal.setAttribute('open', ''); document.body.classList.add('overflow-hidden'); }
    this.goToStep(step || 'fabric', force);
    this.updatePreview();
  };

  SofaConfigurator.prototype.close = function () {
    if (!this.modal) return;
    if (typeof this.modal.hide === 'function') this.modal.hide();
    else { this.modal.removeAttribute('open'); document.body.classList.remove('overflow-hidden'); }
  };

  // Steps the customer can move between — exactly those with a stepper
  // entry. Options with a single value get no entry (nothing to choose).
  SofaConfigurator.prototype.navigableSteps = function () {
    var out = [];
    this.qa('[data-cfg-step-btn]').forEach(function (btn) {
      out.push(btn.dataset.cfgStepBtn);
    });
    return out;
  };

  // `force` reaches a panel that has no stepper entry: a single-fabric
  // product keeps its fabric panel only to host the sample-request UI.
  SofaConfigurator.prototype.goToStep = function (step, force) {
    var navigable = this.navigableSteps();
    var reachable = navigable.indexOf(step) !== -1 ||
      (force && !!this.q('[data-cfg-step="' + step + '"]'));
    if (!reachable) step = navigable[0];
    if (!step) return;
    this.state.step = step;

    this.qa('[data-cfg-step]').forEach(function (panel) {
      panel.classList.toggle('is-active', panel.dataset.cfgStep === step);
    });
    this.qa('[data-cfg-step-btn]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.cfgStepBtn === step);
    });
  };

  /* ── Rendering updates ────────────────────────────────── */

  // Swaps `img`'s src to whatever `container.dataset[datasetKey]` (a JSON
  // fabric→image map, minus the outer braces) has for `fabric`, falling
  // back to the image the markup shipped with. Shared by every "size"
  // summary surface — the popup's size-grid cards, the main-page tile, and
  // the popup rail — so each shows the sofa in the fabric actually chosen
  // instead of a fixed default photo.
  function applyFabricImageMap(container, img, datasetKey, fabric) {
    if (!container || !img || !fabric) return;
    var map = container._cfgImageMap;
    if (!map) {
      try {
        map = JSON.parse('{' + (container.dataset[datasetKey] || '') + '}');
      } catch (e) {
        map = {};
      }
      container._cfgImageMap = map;
    }
    if (!img.dataset.cfgDefaultSrc) {
      img.dataset.cfgDefaultSrc = img.getAttribute('src') || '';
    }
    var next = map[fabric] || img.dataset.cfgDefaultSrc;
    if (next && img.getAttribute('src') !== next) {
      img.setAttribute('src', next);
      img.removeAttribute('srcset');
    }
  }

  // Re-skin the Size grid so every sibling shows the fabric the customer has
  // actually chosen. Siblings with no image for that fabric keep their default.
  SofaConfigurator.prototype.updateSizeCardImages = function () {
    var fabric = this.state.fabric;
    if (!fabric) return;

    this.qa('[data-cfg-size-link]').forEach(function (card) {
      applyFabricImageMap(card, card.querySelector('[data-cfg-size-image]'), 'cfgSizeImages', fabric);
    });

    // The collapsed "Choose your size" tile and the popup rail's size row
    // each carry their OWN per-fabric map (this product's own variants, not
    // the sibling sizes').
    ['[data-cfg-tile-thumb="size"]', '[data-cfg-sum-thumb="size"]'].forEach(function (sel) {
      var el = document.querySelector(sel);
      applyFabricImageMap(el, el && el.querySelector('img'), 'cfgTileImages', fabric);
    });
  };

  SofaConfigurator.prototype.updateAll = function (userDriven) {
    var variant = this.currentVariant();
    if (!variant) return;

    var priceEl = this.q('[data-cfg-price]');
    if (priceEl) {
      var railPence = variant.price;
      if (this.data.bespoke && this.state.bespokeDelta !== 0) railPence += this.data.bespoke.feePrice;
      priceEl.textContent = this.formatMoney(railPence);
    }

    var compareEl = this.q('[data-cfg-compare]');
    if (compareEl) {
      var onSale = variant.compareAtPrice > variant.price;
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = this.formatMoney(variant.compareAtPrice);
    }

    this.setText('[data-cfg-sum="fabric"]', this.state.fabric);
    this.setText('[data-cfg-tile-value="fabric"]', this.state.fabric);
    this.updateSizeCardImages();

    // Additional covering: only submit the property once one is chosen.
    if (this.data.coverings) {
      var covLabel = this.coveringLabel();
      var covActive = this.coveringsActive();
      this.qa('[data-cfg-coverings-input]').forEach(function (input) {
        input.value = covLabel;
        input.disabled = !(covActive && covLabel);
      });
      var covRow = this.q('[data-cfg-covering-row]');
      if (covRow) covRow.hidden = !covActive;
      this.setText('[data-cfg-sum="covering"]', covLabel || 'Not chosen yet');

      var covThumb = this.q('[data-cfg-sum-thumb="covering"]');
      if (covThumb && this.state.covering) {
        var picked = this.state.covering;
        var match = this.coveringItems().filter(function (i) {
          return i.range === picked.range && i.name === picked.colour;
        })[0];
        if (match && match.image) {
          covThumb.style.backgroundImage = 'url(' + match.image + ')';
          covThumb.style.backgroundColor = '';
        } else if (match && match.hex) {
          covThumb.style.backgroundImage = '';
          covThumb.style.backgroundColor = match.hex;
        }
      }
    }
    if (this.state.interior !== null) {
      this.setText('[data-cfg-sum="interior"]', this.state.interior);
      this.setText('[data-cfg-tile-value="interior"]', this.state.interior);
    }

    var meta = this.fabricMeta(this.state.fabric);
    var self = this;
    ['[data-cfg-sum-thumb="fabric"]', '[data-cfg-tile-thumb="fabric"]'].forEach(function (sel) {
      var el = self.q(sel);
      if (!el || !meta) return;
      if (meta.swatchImage || meta.fallbackImage) {
        el.style.backgroundImage = 'url(' + (meta.swatchImage || meta.fallbackImage) + ')';
        el.style.backgroundColor = '';
      } else if (meta.swatchColor) {
        el.style.backgroundImage = '';
        el.style.backgroundColor = 'rgb(' + meta.swatchColor + ')';
      }
    });

    // Bespoke width: summary row + fee included in the rail price
    if (this.data.bespoke) {
      this.setText('[data-cfg-sum="width"]', this.bespokeLabel());
    }

    // Leg finish (line item property, no variant/price impact)
    if (this.state.leg) {
      this.setText('[data-cfg-sum="leg"]', this.state.leg);
      this.setText('[data-cfg-tile-value="leg"]', this.state.leg);
      var legMeta = (this.data.legStyles || []).filter(function (l) { return l.name === self.state.leg; })[0];
      if (legMeta) {
        ['[data-cfg-sum-thumb="leg"]', '[data-cfg-tile-thumb="leg"]'].forEach(function (sel) {
          var el = self.q(sel);
          if (!el) return;
          if (legMeta.thumb) {
            el.style.backgroundImage = 'url(' + legMeta.thumb + ')';
            el.style.backgroundColor = '';
          } else if (legMeta.color) {
            el.style.backgroundImage = '';
            el.style.backgroundColor = legMeta.color;
          }
        });
      }
      this.qa('[data-cfg-leg-input]').forEach(function (input) { input.value = self.state.leg; });
    }

    // Scatter cushions (line item properties, no variant/price impact)
    (this.data.scatterCushions || []).forEach(function (cushion, idx) {
      var chosen = self.state.scatter[idx];
      if (!chosen) return;
      self.setText('[data-cfg-sum="scatter-' + idx + '"]', chosen);
      self.setText('[data-cfg-tile-value="scatter-' + idx + '"]', chosen);
      var meta = self.scatterFabricMeta(chosen);
      ['[data-cfg-sum-thumb="scatter-' + idx + '"]', '[data-cfg-tile-thumb="scatter-' + idx + '"]'].forEach(function (sel) {
        var el = self.q(sel);
        if (!el || !meta) return;
        if (meta.image) {
          el.style.backgroundImage = 'url(' + meta.image + ')';
          el.style.backgroundColor = '';
        } else if (meta.color) {
          el.style.backgroundImage = '';
          el.style.backgroundColor = meta.color;
        }
      });
      self.qa('[data-cfg-scatter-input="' + idx + '"]').forEach(function (input) { input.value = chosen; });
    });

    // Lead time vs in-stock express message, per selected variant
    this.qa('[data-cfg-lead-mto]').forEach(function (el) { el.hidden = !!variant.inStock; });
    this.qa('[data-cfg-lead-stock]').forEach(function (el) { el.hidden = !variant.inStock; });

    this.updatePreview();
    this.updateHeroSpin();
    if (userDriven) this.syncProductForm(variant, false);
  };

  /* ── 360 spin as the native hero gallery image ────────── */

  // When the selected variant has a frameset (and the 360 app's web
  // component is on the page), the spin replaces the hero main image —
  // behaving like a native product image. No separate toggle control: the
  // viewer's own built-in fullscreen button (⛶, top-right — see
  // spin360-viewer.js _toggleFullscreen) is the way to view it large,
  // matching sofaclub.co.uk rather than a bespoke spin/flat-photo switch.
  SofaConfigurator.prototype.updateHeroSpin = function () {
    var heroMain = document.querySelector('[data-cfg-gallery-main]') ||
                   document.querySelector('.sofa-hero__main-image');
    if (!heroMain) return;
    var variant = this.currentVariant();
    var canSpin = variant && variant.frameset &&
      window.customElements && window.customElements.get('spin360-viewer');
    var flat = heroMain.querySelector('[data-cfg-flat]') ||
               heroMain.querySelector('.sofa-hero__zoom-link') ||
               heroMain.querySelector('.sofa-hero__main-img');

    if (canSpin) {
      if (!this.heroSpin || !heroMain.contains(this.heroSpin)) {
        this.heroSpin = document.createElement('spin360-viewer');
        this.heroSpin.className = 'cfg-hero-spin';
        heroMain.appendChild(this.heroSpin);

        var self = this;
        // Clicking a gallery thumbnail switches to that photo (only
        // reachable once the carousel is shown again — canSpin false).
        document.addEventListener('click', function (e) {
          if (self.dead) return;
          if (e.target.closest('.sofa-hero__thumb') || e.target.closest('[data-cfg-thumb]')) {
            self.showHeroSpin(false);
          }
        });
      }
      if (this.heroSpinLoadedFor !== variant.id) {
        this.heroSpin.load(variant.frameset);
        this.heroSpinLoadedFor = variant.id;
      }
      this.heroFlat = flat;
      this.ensureSpinThumb(true);
      this.showHeroSpin(true);
    } else {
      this.ensureSpinThumb(false);
      if (this.heroSpin && heroMain.contains(this.heroSpin)) {
        this.showHeroSpin(false);
      }
    }
  };

  // A "360°" tile at the front of the thumbnail strip — the spin behaves
  // like the first gallery image, and stays one click away after the
  // shopper browses the photo thumbs.
  SofaConfigurator.prototype.ensureSpinThumb = function (canSpin) {
    var thumbs = document.querySelector('.cfg-gallery__thumbs');
    if (!thumbs) return;
    var spinThumb = thumbs.querySelector('[data-cfg-thumb-spin]');
    if (!canSpin) {
      if (spinThumb) spinThumb.remove();
      return;
    }
    if (!spinThumb) {
      spinThumb = document.createElement('button');
      spinThumb.type = 'button';
      spinThumb.className = 'cfg-gallery__thumb cfg-gallery__thumb--spin';
      spinThumb.setAttribute('data-cfg-thumb-spin', '');
      spinThumb.setAttribute('aria-label', 'View 360 degree spin');
      spinThumb.innerHTML =
        '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M21.5 2v6h-6"/><path d="M21.34 8A10 10 0 1 0 22 12"/></svg>' +
        '<span>360&deg;</span>';
      var self = this;
      spinThumb.addEventListener('click', function () { self.showHeroSpin(true); });
      thumbs.insertBefore(spinThumb, thumbs.firstChild);
    }
  };

  SofaConfigurator.prototype.showHeroSpin = function (show) {
    this.heroSpinVisible = !!show;
    if (this.heroSpin) this.heroSpin.style.display = show ? '' : 'none';
    if (this.heroFlat) this.heroFlat.style.display = show ? 'none' : '';
    var spinThumb = document.querySelector('[data-cfg-thumb-spin]');
    if (spinThumb) spinThumb.classList.toggle('is-active', !!show);
    if (show) {
      document.querySelectorAll('[data-cfg-thumb]').forEach(function (t) {
        t.classList.remove('is-active');
      });
    }

    // The native photo carousel (thumb strip + prev/next arrows) has
    // nothing useful to do while the 360 spin is showing — the spin's own
    // drag/scrub and built-in fullscreen button replace it. Hide it fully
    // rather than leaving it active behind the spin.
    var thumbs = document.querySelector('.cfg-gallery__thumbs');
    if (thumbs) thumbs.hidden = !!show;
    document.querySelectorAll('.cfg-gallery__arrow').forEach(function (arrow) {
      arrow.hidden = !!show;
    });
  };

  /* == Bespoke made-to-measure width ====================== */

  SofaConfigurator.prototype.initBespoke = function () {
    if (!this.data.bespoke) return;
    var self = this;
    var b = this.data.bespoke;
    // Panel visibility follows this EXPLICIT mode, not the delta — sliding
    // back through 0 must not collapse the panel under the cursor.
    this.bespokeUIMode = 'standard';

    // Preset chips: -max … Standard … +max
    var presets = this.q('[data-cfg-bespoke-presets]');
    if (presets) {
      var stops = [-b.max, -Math.round(b.max / 2), 0, Math.round(b.max / 2), b.max]
        .filter(function (v, i, a) { return a.indexOf(v) === i; });
      presets.innerHTML = stops.map(function (v) {
        var label = v === 0 ? 'Standard' : (v > 0 ? '+' + v + 'cm' : v + 'cm');
        return '<button type="button" class="cfg-bespoke-preset" data-cfg-bespoke-preset="' + v + '">' + label + '</button>';
      }).join('');
    }

    var slider = this.q('[data-cfg-bespoke-slider]');
    var input = this.q('[data-cfg-bespoke-input]');
    if (slider) slider.addEventListener('input', function () { self.setBespokeDelta(parseInt(slider.value, 10)); });
    if (input) input.addEventListener('change', function () { self.setBespokeDelta(parseInt(input.value, 10) || 0); });
    var tick = this.q('[data-cfg-bespoke-tick]');
    if (tick) tick.addEventListener('change', function () {
      self.state.bespokeTicked = tick.checked;
      var row = self.q('[data-cfg-bespoke-tick-row]');
      if (row && tick.checked) row.classList.remove('is-error');
    });

    // Intercept Add to Basket: bespoke orders add the sofa AND the fee
    // product in one call, paired by a shared reference property.
    document.addEventListener('submit', function (e) {
      if (self.dead) return;
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form || self.state.bespokeDelta === 0) return;
      e.preventDefault();
      if (!self.state.bespokeTicked) {
        self.open('width');
        var row = self.q('[data-cfg-bespoke-tick-row]');
        if (row) { row.classList.add('is-error'); row.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        return;
      }
      self.addBespokeToCart(form);
    });

    this.renderBespoke();
  };

  SofaConfigurator.prototype.setBespokeMode = function (mode) {
    this.bespokeUIMode = mode;
    if (mode === 'standard') {
      this.setBespokeDelta(0);
    } else {
      this.renderBespoke();
    }
  };

  SofaConfigurator.prototype.setBespokeDelta = function (delta) {
    var b = this.data.bespoke;
    if (!b) return;
    delta = Math.max(-b.max, Math.min(b.max, isNaN(delta) ? 0 : delta));
    this.state.bespokeDelta = delta;
    if (delta !== 0) this.bespokeUIMode = 'bespoke';
    this.renderBespoke();
    this.updateAll(false);
  };

  SofaConfigurator.prototype.bespokeLabel = function () {
    var b = this.data.bespoke;
    var d = this.state.bespokeDelta;
    if (!b || d === 0) return 'Standard';
    var out = (d > 0 ? '+' : '') + d + 'cm';
    if (b.standardWidth) out = (b.standardWidth + d) + 'cm (' + out + ')';
    return out;
  };

  SofaConfigurator.prototype.renderBespoke = function () {
    var b = this.data.bespoke;
    if (!b) return;
    var d = this.state.bespokeDelta;
    var bespokeActive = this.bespokeUIMode === 'bespoke';

    var panel = this.q('[data-cfg-bespoke-panel]');
    if (panel) panel.hidden = !bespokeActive;

    this.qa('[data-cfg-width-mode]').forEach(function (card) {
      card.classList.toggle('is-selected',
        (card.dataset.cfgWidthMode === 'bespoke') === bespokeActive);
    });

    var slider = this.q('[data-cfg-bespoke-slider]');
    var input = this.q('[data-cfg-bespoke-input]');
    if (slider) slider.value = d;
    if (input) input.value = d;
    this.qa('.cfg-bespoke-preset').forEach(function (p) {
      p.classList.toggle('is-active', parseInt(p.dataset.cfgBespokePreset, 10) === d);
    });

    var fee = this.q('[data-cfg-bespoke-fee]');
    if (fee) fee.hidden = d === 0;
    var tickRow = this.q('[data-cfg-bespoke-tick-row]');
    if (tickRow) {
      tickRow.hidden = d === 0;
      if (d === 0) tickRow.classList.remove('is-error');
    }

    this.drawBespokeSofa();
  };

  // Fixed arms, stretching middle: the honest way to visualise width.
  SofaConfigurator.prototype.drawBespokeSofa = function () {
    var svg = this.q('.cfg-bespoke-svg');
    var b = this.data.bespoke;
    if (!svg || !b) return;
    var d = this.state.bespokeDelta;
    var CX = 320, PXCM = 120 / Math.max(20, b.max * 2);
    var baseW = 384, w = baseW + d * PXCM * 2;
    var left = CX - w / 2, right = CX + w / 2;
    var armW = 34, backSide = 60, gap = 4;
    function set(name, attrs) {
      var el = svg.querySelector('[data-bsp="' + name + '"]');
      if (!el) return;
      Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
    }
    var bodyL = left + armW - 12, bodyR = right - armW + 12;
    set('armL', { x: left });
    set('armR', { x: right - armW });
    set('seat', { x: bodyL, width: bodyR - bodyL });
    set('backL', { x: bodyL });
    set('backM', { x: bodyL + backSide + gap, width: Math.max(40, bodyR - bodyL - 2 * (backSide + gap)) });
    set('backR', { x: bodyR - backSide });
    set('legL', { x: bodyL + 14 });
    set('legR', { x: bodyR - 24 });
    set('dimL', { x1: left, x2: left });
    set('dimR', { x1: right, x2: right });
    set('dimBar', { x1: left, x2: right });
    set('dimBadge', { x: CX - 44, width: 88 });
    var label = svg.querySelector('[data-bsp="dimText"]');
    if (label) label.textContent = this.bespokeLabel();
  };

  SofaConfigurator.prototype.addBespokeToCart = function (form) {
    var self = this;
    var b = this.data.bespoke;
    var variant = this.currentVariant();
    if (!variant) return;
    var ref = 'bsp-' + Date.now();
    var widthLabel = this.bespokeLabel();
    var forLabel = this.data.product.title + ' — ' + this.state.fabric;
    var props = { 'Bespoke Width': widthLabel, '_bespoke_ref': ref };
    if (this.state.leg) props[this.data.legPropertyName] = this.state.leg;
    var payload = { items: [
      { id: variant.id, quantity: 1, properties: props },
      { id: b.feeVariantId, quantity: 1, properties: {
          'For': forLabel, 'Width': widthLabel, '_bespoke_ref': ref } }
    ] };
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed');
      window.location.href = '/cart';
    }).catch(function () {
      if (btn) btn.disabled = false;
      form.submit();
    });
  };

  SofaConfigurator.prototype.setText = function (selector, value) {
    this.qa(selector).forEach(function (el) { el.textContent = value; });
  };

  // Preview: 360 spin when the variant has a frameset and the app's web
  // component is registered; otherwise the flat variant image.
  SofaConfigurator.prototype.updatePreview = function () {
    var variant = this.currentVariant();
    if (!variant) return;

    var spinHost = this.q('[data-cfg-spin]');
    var img = this.q('[data-cfg-preview-image]');
    var badge = this.q('[data-cfg-spin-badge]');
    var canSpin = variant.frameset && window.customElements && window.customElements.get('spin360-viewer');

    if (canSpin) {
      if (!this.spinViewer) {
        this.spinViewer = document.createElement('spin360-viewer');
        spinHost.appendChild(this.spinViewer);
      }
      if (this.loadedFramesetFor !== variant.id) {
        this.spinViewer.load(variant.frameset);
        this.loadedFramesetFor = variant.id;
      }
      spinHost.hidden = false;
      if (badge) badge.hidden = false;
      if (img) img.hidden = true;
    } else {
      if (spinHost) spinHost.hidden = true;
      if (badge) badge.hidden = true;
      if (img) {
        img.hidden = false;
        var src = variant.image || this.data.product.featuredImage;
        if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
      }
    }
  };

  /* ── PDP sync ─────────────────────────────────────────── */

  SofaConfigurator.prototype.syncProductForm = function (variant, replaceUrl) {
    // Hidden inputs on any product form for this product (sofa-hero + sticky ATC)
    document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
      input.value = variant.id;
    });

    // Native selects kept for fallback (fabric-picker pattern)
    var nativeSelect = document.getElementById('fabric-picker-native-select');
    if (nativeSelect) nativeSelect.value = variant.id;

    // Page price — ALL matches, since the sticky bar carries a second one.
    var self = this;
    var priceEls = document.querySelectorAll('[data-cfg-page-price]');
    if (priceEls.length) {
      priceEls.forEach(function (el) { el.textContent = self.formatMoney(variant.price); });
    } else {
      var heroPrice = document.querySelector('.sofa-hero__price');
      if (heroPrice) heroPrice.textContent = this.formatMoney(variant.price);
    }
    var pageCompare = document.querySelector('[data-cfg-page-compare]');
    if (pageCompare) {
      var onSale = variant.compareAtPrice > variant.price;
      pageCompare.hidden = !onSale;
      if (onSale) pageCompare.textContent = this.formatMoney(variant.compareAtPrice);
    }

    // Sticky bar thumbnail — follows the selected variant's own image
    var stickyImage = variant.image || this.data.product.featuredImage;
    if (stickyImage) {
      document.querySelectorAll('[data-cfg-sticky-image]').forEach(function (img) {
        if (img.getAttribute('src') !== stickyImage) img.setAttribute('src', stickyImage);
      });
    }

    // Selected fabric name — sticky bar (and anywhere else it's rendered)
    var fabricEls = document.querySelectorAll('[data-cfg-page-fabric]');
    if (fabricEls.length) {
      fabricEls.forEach(function (el) { el.textContent = self.state.fabric; });
    }

    this.updateSpinGrid(variant);

    // URL param
    var params = new URLSearchParams(window.location.search);
    params.set('variant', variant.id);
    var method = replaceUrl ? 'replaceState' : 'replaceState';
    window.history[method]({}, '', window.location.pathname + '?' + params.toString());

    // Broadcast for the 360 gallery viewer, analytics, etc.
    document.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { variant: { id: variant.id, price: variant.price, title: variant.title } }
    }));
  };

  SofaConfigurator.prototype.confirm = function () {
    // Sample-request mode owns the panel; the sofa's own Confirm is inert.
    if (this.swatchRequestOpen()) return;

    var variant = this.currentVariant();
    if (variant) this.syncProductForm(variant, false);

    // Picking the coverings option without choosing an actual covering
    // would cart an uplift with nothing specifying what to make it in.
    if (this.coveringsActive() && !this.state.covering) {
      this.goToStep('fabric');
      var cov = this.q('[data-cfg-coverings]');
      if (cov) {
        cov.classList.add('is-error');
        cov.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      return;
    }

    // Bespoke orders must tick the measurements confirmation (in the rail,
    // directly above this button) before anything goes in the basket.
    if (this.data.bespoke && this.state.bespokeDelta !== 0 && !this.state.bespokeTicked) {
      var row = this.q('[data-cfg-bespoke-tick-row]');
      if (row) {
        row.classList.add('is-error');
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return;
    }

    this.close();

    // Swap the main image to the confirmed fabric's variant image
    var heroImg = document.querySelector('[data-cfg-main-image]') ||
                  document.getElementById('sofa-main-image');
    if (heroImg && variant && variant.image) {
      heroImg.src = variant.image;
      heroImg.removeAttribute('srcset');
    }
    this.updateHeroSpin();

    // Confirm just applies the selection to the main page (variant id,
    // price, image, leg/bespoke properties are already synced live).
    // Adding to the basket is a separate, explicit step on the PDP itself.
  };

  /* ── Init ─────────────────────────────────────────────── */

  function boot() {
    document.querySelectorAll('[data-sofa-configurator]').forEach(function (root) {
      if (!root.dataset.cfgReady) {
        root.dataset.cfgReady = 'true';
        try {
          window.__cfgInstance = new SofaConfigurator(root);
        } catch (e) {
          window.__cfgError = (e && e.stack) || String(e);
          if (window.console) console.error('SofaConfigurator failed to initialise', e);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: sections are re-rendered on every setting change.
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target.querySelector || !e.target.querySelector('[data-sofa-configurator]')) return;
    if (window.__cfgInstance) window.__cfgInstance.dead = true;
    delete window.__cfgInstance;
    // Stale body-level modals from the previous render
    document.querySelectorAll('body > [data-cfg-modal]').forEach(function (el) {
      if (!e.target.contains(el)) el.remove();
    });
    boot();
  });
})();
